import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { 
  ContractAction, 
  ContractActionStatus,
  ContractActionDocument 
} from '../models/contract-action.model';
import { ContractInvolvedEntity } from '../models/contract.model';

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data?: T;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

/**
 * Create Action Request DTO
 */
export interface CreateActionRequest {
  actionType: string;
  actionName?: string;         // Human-readable action name
  description?: string;
  notes?: string;              // Generic notes
  selectedTemplateId?: string;
  
  // Type-specific fields
  newEndDate?: string;         // For MODIFY_DURATION
  newContractSubject?: string;  // For MODIFY_SUBJECT
  newCurrency?: string;         // For MODIFY_ECONOMICS (valuta)
  newTotalContractValue?: number; // For MODIFY_ECONOMICS (valore durata contratto IVA esclusa)
  newAnnualValue?: number;      // For MODIFY_ECONOMICS (valore annuale IVA esclusa)
  newInvolvedEntities?: ContractInvolvedEntity[]; // For EXTEND_PARTIES
  terminationReason?: string;  // For TERMINATION
}

@Injectable({
  providedIn: 'root'
})
export class ContractActionService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.backofficeApiUrl}/api/v1`;

  /**
   * Get HTTP headers with user ID
   */
  private getHeaders(userId?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    if (userId) {
      headers = headers.set('X-User-Id', userId);
    }
    
    return headers;
  }

  /**
   * Get all actions for a contract
   * GET /api/v1/contracts/{contractId}/actions
   */
  getActionsByContractId(contractId: string | number, status?: string): Observable<ApiResponse<ContractAction[]>> {
    let url = `${this.baseUrl}/contracts/${contractId}/actions`;
    
    if (status) {
      url += `?status=${status}`;
    }
    
    console.log('📡 ContractActionService - Getting actions:', url);
    return this.http.get<ApiResponse<ContractAction[]>>(url);
  }

  /**
   * Get action details by ID
   * GET /api/v1/contracts/{contractId}/actions/{actionId}
   */
  getActionById(contractId: string | number, actionId: number): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}`;
    console.log('📡 ContractActionService - Getting action:', url);
    return this.http.get<ApiResponse<ContractAction>>(url);
  }

  /**
   * Create new action
   * POST /api/v1/contracts/{contractId}/actions
   */
  createAction(contractId: string | number, request: CreateActionRequest, userId?: string): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions`;
    const headers = this.getHeaders(userId);
    
    console.log('📡 ContractActionService - Creating action:', url, request);
    return this.http.post<ApiResponse<ContractAction>>(url, request, { headers });
  }

  /**
   * Update action
   * PUT /api/v1/contracts/{contractId}/actions/{actionId}
   * Note: Only DRAFT actions can be updated
   */
  updateAction(
    contractId: string | number, 
    actionId: number, 
    request: Partial<CreateActionRequest>, 
    userId?: string
  ): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}`;
    const headers = this.getHeaders(userId);
    
    console.log('📡 ContractActionService - Updating action:', url, request);
    return this.http.put<ApiResponse<ContractAction>>(url, request, { headers });
  }

  /**
   * Upload signed document to action
   * POST /api/v1/contracts/{contractId}/actions/{actionId}/signed-document
   */
  uploadSignedDocument(
    contractId: string | number, 
    actionId: number, 
    file: File, 
    userId?: string
  ): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}/document`;
    
    const formData = new FormData();
    formData.append('file', file);
    
    let headers = new HttpHeaders();
    if (userId) {
      headers = headers.set('X-User-Id', userId);
    }
    // Non impostare Content-Type - FormData lo gestisce automaticamente con boundary
    
    console.log('📡 ContractActionService - Uploading signed document:', url);
    return this.http.post<ApiResponse<ContractAction>>(url, formData, { headers });
  }

  /**
   * Delete signed document from action
   * DELETE /api/v1/contracts/{contractId}/actions/{actionId}/document
   */
  deleteSignedDocument(
    contractId: string | number, 
    actionId: number, 
    userId?: string
  ): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}/document`;
    
    const options = {
      headers: userId ? new HttpHeaders({ 'X-User-Id': userId }) : undefined
    };
    
    console.log('📡 ContractActionService - Deleting signed document:', url);
    return this.http.delete<ApiResponse<ContractAction>>(url, options);
  }

  /**
   * Activate action - applies changes to contract
   * POST /api/v1/contracts/{contractId}/actions/{actionId}/activate
   * ⚠️ This endpoint applies the action's changes to the contract immediately
   */
  activateAction(
    contractId: string | number, 
    actionId: number, 
    userId?: string
  ): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}/activate`;
    
    const options = {
      headers: userId ? new HttpHeaders({ 'X-User-Id': userId }) : undefined
    };
    
    console.log('📡 ContractActionService - Activating action:', url);
    return this.http.post<ApiResponse<ContractAction>>(url, {}, options);
  }

  /**
   * Complete action - marks as completed
   * POST /api/v1/contracts/{contractId}/actions/{actionId}/complete
   */
  completeAction(
    contractId: string | number, 
    actionId: number, 
    userId?: string
  ): Observable<ApiResponse<ContractAction>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}/complete`;
    
    const options = {
      headers: userId ? new HttpHeaders({ 'X-User-Id': userId }) : undefined
    };
    
    console.log('📡 ContractActionService - Completing action:', url);
    return this.http.post<ApiResponse<ContractAction>>(url, {}, options);
  }

  /**
   * Delete action
   * DELETE /api/v1/contracts/{contractId}/actions/{actionId}
   * Note: Only DRAFT actions can be deleted
   */
  deleteAction(
    contractId: string | number, 
    actionId: number
  ): Observable<ApiResponse<void>> {
    const url = `${this.baseUrl}/contracts/${contractId}/actions/${actionId}`;
    
    console.log('📡 ContractActionService - Deleting action:', url);
    return this.http.delete<ApiResponse<void>>(url);
  }

  /**
   * Get all actions globally (across all contracts)
   * GET /api/v1/actions
   */
  getAllActions(status?: string, actionType?: string): Observable<ApiResponse<ContractAction[]>> {
    let url = `${this.baseUrl}/actions`;
    const params: string[] = [];
    
    if (status) {
      params.push(`status=${status}`);
    }
    if (actionType) {
      params.push(`actionType=${actionType}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    console.log('📡 ContractActionService - Getting all actions:', url);
    return this.http.get<ApiResponse<ContractAction[]>>(url);
  }

  /**
   * Legacy methods kept for backward compatibility - deprecated
   * @deprecated Use getActionsByContractId instead
   */
  getDocumentsByActionId(actionId: number): Observable<ContractActionDocument[]> {
    console.warn('⚠️ getDocumentsByActionId is deprecated');
    return this.http.get<ContractActionDocument[]>(`${this.baseUrl}/actions/${actionId}/documents`);
  }

  /**
   * Legacy method - deprecated
   * @deprecated Use uploadSignedDocument instead
   */
  uploadDocument(actionId: number, file: File, category: string): Observable<ContractActionDocument> {
    console.warn('⚠️ uploadDocument is deprecated, use uploadSignedDocument instead');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    return this.http.post<ContractActionDocument>(`${this.baseUrl}/actions/${actionId}/documents`, formData);
  }
}
